/**
 * Prefetch de chunks de rotas lazy + métricas de acerto.
 *
 * Vite deduplica imports dinâmicos pela mesma URL, então chamar
 * `import("@/pages/Discover")` aqui aproveita o mesmo chunk que
 * `lazy(() => import("@/pages/Discover"))` em App.tsx — sem download
 * duplicado.
 *
 * Estratégia conservadora: prefetch só após idle ou em sinal claro de
 * intenção do usuário (hover, focus, touchstart). Cada destino baixa
 * no máximo uma vez por sessão.
 *
 * Métricas (sessionStorage `zappy.prefetch.metrics`):
 *  - hit:   navegação para rota que já tinha sido prefetchada
 *  - miss:  navegação para rota conhecida no registro mas sem prefetch
 *  - cold:  navegação para rota fora do registro (não rastreável)
 *  Cada navegação também despacha `window` event `prefetch-metric`
 *  com `{ path, outcome, ageMs }` para observadores (devtools, A/B).
 */

type Importer = () => Promise<unknown>;
type Outcome = "hit" | "miss" | "cold";

const importers: Record<string, Importer> = {
  "/auth": () => import("@/pages/Auth"),
  "/venue": () => import("@/pages/VenueDetail"),
  "/discover": () => import("@/pages/Discover"),
  "/venues": () => import("@/pages/Venues"),
  "/matches": () => import("@/pages/Matches"),
  "/profile": () => import("@/pages/Profile"),
  "/who-liked-you": () => import("@/pages/WhoLikedYou"),
  "/events": () => import("@/pages/Events"),
  "/my-subscription": () => import("@/pages/MySubscription"),
  "/chat": () => import("@/pages/Chat"),
  "/plans": () => import("@/pages/Plans"),
  "/venue-management": () => import("@/pages/VenueManagement"),
  "/venue-dashboard": () => import("@/pages/VenueDashboard"),
};

// path -> timestamp de quando o prefetch foi disparado
const prefetched = new Map<string, number>();
const STORAGE_KEY = "zappy.prefetch.metrics";

export interface PrefetchStats {
  hit: number;
  miss: number;
  cold: number;
  totalAgeMs: number;
  hitsByRoute: Record<string, number>;
  missesByRoute: Record<string, number>;
}

function emptyStats(): PrefetchStats {
  return { hit: 0, miss: 0, cold: 0, totalAgeMs: 0, hitsByRoute: {}, missesByRoute: {} };
}

function readStats(): PrefetchStats {
  if (typeof window === "undefined") return emptyStats();
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyStats();
    return { ...emptyStats(), ...(JSON.parse(raw) as PrefetchStats) };
  } catch {
    return emptyStats();
  }
}

function writeStats(stats: PrefetchStats): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stats));
  } catch {
    /* quota / privacy mode — ignorar */
  }
}

function resolveKey(path: string): string | null {
  const clean = path.split("?")[0].split("#")[0];
  if (importers[clean]) return clean;
  const match = Object.keys(importers).find((k) => clean.startsWith(k + "/"));
  return match ?? null;
}

export function prefetchRoute(path: string): void {
  const key = resolveKey(path);
  if (!key || prefetched.has(key)) return;
  prefetched.set(key, Date.now());
  try {
    importers[key]().catch(() => prefetched.delete(key));
  } catch {
    prefetched.delete(key);
  }
}

export function prefetchRoutesIdle(paths: string[]): void {
  if (typeof window === "undefined") return;
  const run = () => paths.forEach(prefetchRoute);
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
    .requestIdleCallback;
  if (ric) ric(run, { timeout: 2000 });
  else setTimeout(run, 800);
}

/**
 * Registra uma navegação e classifica como hit/miss/cold.
 * Deve ser chamada uma vez por mudança de rota.
 */
export function recordNavigation(path: string): Outcome {
  const key = resolveKey(path);
  let outcome: Outcome;
  let ageMs = 0;

  if (!key) {
    outcome = "cold";
  } else if (prefetched.has(key)) {
    outcome = "hit";
    ageMs = Date.now() - (prefetched.get(key) ?? Date.now());
  } else {
    outcome = "miss";
  }

  const stats = readStats();
  stats[outcome] += 1;
  if (outcome === "hit" && key) {
    stats.totalAgeMs += ageMs;
    stats.hitsByRoute[key] = (stats.hitsByRoute[key] ?? 0) + 1;
  } else if (outcome === "miss" && key) {
    stats.missesByRoute[key] = (stats.missesByRoute[key] ?? 0) + 1;
  }
  writeStats(stats);

  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(
        new CustomEvent("prefetch-metric", {
          detail: { path, route: key ?? null, outcome, ageMs },
        }),
      );
    } catch {
      /* ignore */
    }
  }

  return outcome;
}

export function getPrefetchStats(): PrefetchStats & { hitRate: number; avgAgeMs: number } {
  const s = readStats();
  const tracked = s.hit + s.miss;
  return {
    ...s,
    hitRate: tracked > 0 ? s.hit / tracked : 0,
    avgAgeMs: s.hit > 0 ? Math.round(s.totalAgeMs / s.hit) : 0,
  };
}

export function resetPrefetchStats(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

// Expor no window para debug em produção: `__zappyPrefetch.stats()`
if (typeof window !== "undefined") {
  (window as unknown as { __zappyPrefetch?: unknown }).__zappyPrefetch = {
    stats: getPrefetchStats,
    reset: resetPrefetchStats,
    prefetch: prefetchRoute,
  };
}
